import { useBulkDownloadFileMutation } from '@/hooks/useDownloadFileMutation';
import Download from '@/images/Download.svg';
import { useAnalytics } from '@/services/analytics';
import { MixPanelEvent } from '@/utils/types';
import { getItemPresenterTypeAndLabels } from '@/utils/utils';
import { LibraryItem, LibraryItemStatus } from '@d-id/types';
import MenuItem from '@mui/material/MenuItem';
import { ListItemIcon, ListItemText } from './components';

export interface VideoItem {
    id: string;
    name: string;
    language: string;
    videoUrl?: string;
    subtitlesUrl?: string;
}
interface Props {
    group: LibraryItem;
    closeMenu: () => void;
    refetch: () => Promise<{ data: VideoItem[] }>;
}

export const DownloadGroupMenuItem = ({ group, closeMenu, refetch }: Props) => {
    const mixpanel = useAnalytics();

    const onCancelDownload = () =>
        mixpanel?.track('options-download', {
            event: MixPanelEvent.CANCEL,
            groupId: group?.id,
            ...getItemPresenterTypeAndLabels(group as LibraryItem),
        });
    const { mutateAsync: downloadBulk } = useBulkDownloadFileMutation(onCancelDownload, group.name, false);

    if (group.status !== LibraryItemStatus.Done) {
        return null;
    }

    return (
        <MenuItem
            onClick={() => {
                refetch().then(videosGroup => {
                    const videos = (videosGroup?.data ?? []).flatMap(video => {
                        if (!video?.videoUrl) {
                            return [];
                        }

                        return {
                            dataurl: video.videoUrl,
                            filename: `${group.name}_${video.language}`,
                            extension: '.mp4',
                        };
                    });

                    const subtitles = (videosGroup?.data ?? []).flatMap(video => {
                        if (!video?.subtitlesUrl) {
                            return [];
                        }

                        return {
                            dataurl: video.subtitlesUrl,
                            filename: `${group.name}_${video.language}`,
                            extension: '.srt',
                        };
                    });

                    const allFiles = [
                        ...videos,
                        ...subtitles,
                        ...(group?.video_url ? [{ dataurl: group.video_url, filename: group.name, extension: '.mp4' }] : []),
                    ];

                    if (!allFiles?.length) {
                        return;
                    }

                    downloadBulk(allFiles);

                    mixpanel?.track('options-download', {
                        event: MixPanelEvent.CLICK,
                        groupId: group.id,
                        languagesCount: videos?.length ?? 0,
                        ...getItemPresenterTypeAndLabels(group as LibraryItem),
                    });

                    closeMenu();
                });
            }}>
            <ListItemIcon>
                <img src={Download} />
            </ListItemIcon>
            <ListItemText>Download</ListItemText>
        </MenuItem>
    );
};
